import Axios, { AxiosResponse } from 'axios';
import PropTypes from 'prop-types';
import { Action } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { ConfigBase } from '../../types/config';

import { OfferDetailsDTO } from '../../types/dto/OfferDetailsDTO';
import { OfferShortDTO } from '../../types/dto/OfferShortDTO';
import { RequestVoucherDTO } from '../../types/dto/RequestVoucherDTO';
import { VoucherDTO } from '../../types/dto/VoucherDTO';
import { Utils } from '../../utils';
import ApiHelper from '../ApiHelper';
import { AuthState } from '../auth/Types';
import * as Actions from './Actions';
import { OfferError, OfferErrorInfo, OfferState } from './Types';

type RootSubState = {
    authState: AuthState,
    offerState: OfferState,
};

type CommonAppThunkAction<R> = ThunkAction<R, RootSubState, undefined, Action>;
type CommonAppThunkDispatch = ThunkDispatch<RootSubState, undefined, Action>;

export type ServiceActions = {
    allOffers: () => Promise<OfferShortDTO[]>,
    getOffer: (code: number) => Promise<OfferDetailsDTO>,
    requestVoucher: (data: RequestVoucherDTO) => Promise<void>,
    getVoucher: (vehcode: number, offerCode: number) => Promise<VoucherDTO>,
    printVoucher: (vehcode: number, offerCode: number) => Promise<Blob>,
};

export const ServiceActionPropTypes = PropTypes.shape({
    allOffers: PropTypes.func.isRequired,
    getOffer: PropTypes.func.isRequired,
    requestVoucher: PropTypes.func.isRequired,
    getVoucher: PropTypes.func.isRequired,
    printVoucher: PropTypes.func.isRequired,
});

export const createServiceActions = (config: ConfigBase) => {
    const apiHelper = ApiHelper(config);

    const allOffers = (): CommonAppThunkAction<Promise<OfferShortDTO[]>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.allOffersRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/offers/getalloffers/`, null));
                const response = axiosResponse.data as OfferShortDTO[];
                dispatch(Actions.allOffersSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.allOffersFailure());
                throw error;
            }
        };
    };

    const getOffer = (code: number): CommonAppThunkAction<Promise<OfferDetailsDTO>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.getOfferRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/offers/getoffer?code=${code}`, null));
                const response = axiosResponse.data as OfferDetailsDTO;
                dispatch(Actions.getOfferSuccess(response));
                return response;
            } catch (error) {
                dispatch(apiHelper.logFrontendError(error));
                try {
                    const anyError = error as any;
                    const offerErrorInfo: OfferErrorInfo = {
                        response: anyError?.response,
                        errorCode: config.Constants.ErrorCodes.UnknownError,
                        errorMessage: null,
                        exceptionType: null,
                    };

                    offerErrorInfo.errorCode = config.Constants.ErrorCodes.UnknownError;
                    if (Axios.isAxiosError(error) && error.response) {
                        offerErrorInfo.errorMessage = `${error.response.status} ${error.response.statusText}`;
                        offerErrorInfo.exceptionType = error.response.data.ExceptionType;
                    } else {
                        offerErrorInfo.errorMessage = anyError?.message;
                    }
                    dispatch(Actions.getOfferFailure(offerErrorInfo));
                    throw new OfferError(offerErrorInfo);
                } catch (subError) {
                    if (subError instanceof OfferError) {
                        throw subError;
                    } else {
                        throw error;
                    }
                }
            }
        };
    };

    const requestVoucher = (data: RequestVoucherDTO): CommonAppThunkAction<Promise<void>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.requestVoucherRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.post(`/api/${config.ResourceCode}/offers/requestvoucher`, data));
                Utils.checkHttpStatus(axiosResponse);
                dispatch(Actions.requestVoucherSuccess());
            } catch (error) {
                dispatch(apiHelper.logFrontendError(error));
                try {
                    const anyError = error as any;
                    const offerErrorInfo: OfferErrorInfo = {
                        response: anyError?.response,
                        errorCode: config.Constants.ErrorCodes.UnknownError,
                        errorMessage: null,
                        exceptionType: null,
                    };

                    offerErrorInfo.errorCode = config.Constants.ErrorCodes.UnknownError;
                    if (Axios.isAxiosError(error) && error.response) {
                        offerErrorInfo.errorMessage = `${error.response.status} ${error.response.statusText}`;
                        offerErrorInfo.exceptionType = error.response.data.ExceptionType;
                    } else {
                        offerErrorInfo.errorMessage = anyError?.message;
                    }
                    dispatch(Actions.requestVoucherFailure(offerErrorInfo));
                    throw new OfferError(offerErrorInfo);
                } catch (subError) {
                    if (subError instanceof OfferError) {
                        throw subError;
                    } else {
                        throw error;
                    }
                }
            }
        };
    };

    const getVoucher = (vehcode: number, offerCode: number): CommonAppThunkAction<Promise<VoucherDTO>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.getVoucherRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/offers/getvoucher?vehcode=${vehcode}&offerCode=${offerCode}`, null));
                const response = axiosResponse.data as VoucherDTO;
                dispatch(Actions.getVoucherSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.getVoucherFailure());
                throw error;
            }
        };
    };

    const printVoucher = (vehcode: number, offerCode: number): CommonAppThunkAction<Promise<Blob>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.printVoucherRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/offers/printvoucher?vehcode=${vehcode}&offerCode=${offerCode}`, null));
                const response = axiosResponse.data as Blob;
                dispatch(Actions.printVoucherSuccess(response));
                return response;
            } catch (error) {
                dispatch(apiHelper.logFrontendError(error));
                try {
                    const anyError = error as any;
                    const offerErrorInfo: OfferErrorInfo = {
                        response: anyError?.response,
                        errorCode: config.Constants.ErrorCodes.UnknownError,
                        errorMessage: null,
                        exceptionType: null,
                    };

                    offerErrorInfo.errorCode = config.Constants.ErrorCodes.UnknownError;
                    if (Axios.isAxiosError(error) && error.response) {
                        offerErrorInfo.errorMessage = `${error.response.status} ${error.response.statusText}`;
                        offerErrorInfo.exceptionType = error.response.data.ExceptionType;
                    } else {
                        offerErrorInfo.errorMessage = anyError?.message;
                    }
                    dispatch(Actions.printVoucherFailure(offerErrorInfo));
                    throw new OfferError(offerErrorInfo);
                } catch (subError) {
                    if (subError instanceof OfferError) {
                        throw subError;
                    } else {
                        throw error;
                    }
                }
            }
        };
    };

    return {
        allOffers,
        getOffer,
        requestVoucher,
        getVoucher,
        printVoucher,
    };
};

export const bindServiceActionCreators = (config: ConfigBase, dispatch: CommonAppThunkDispatch): ServiceActions => {
    const services = createServiceActions(config);
    return {
        allOffers: () => dispatch(services.allOffers()),
        getOffer: (code: number) => dispatch(services.getOffer(code)),
        requestVoucher: (data: RequestVoucherDTO) => dispatch(services.requestVoucher(data)),
        getVoucher: (vehcode: number, offerCode: number) => dispatch(services.getVoucher(vehcode, offerCode)),
        printVoucher: (vehcode: number, offerCode: number) => dispatch(services.printVoucher(vehcode, offerCode)),
    };
};
